import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/common/PageHeader";
import SmartLoader from "@/components/common/SmartLoader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatDateFr, formatDateTimeFr } from "@/lib/format";
import { ROLES, TID } from "@/constants/testIds";
import { Trash2, UserPlus, Save, Upload, User, KeyRound, Bell, Building2, Shield } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user, organizations, activeOrgId, activeRole, refresh } = useAuth();
  const activeOrg = organizations.find((o) => o.id === activeOrgId);
  const canAdmin = ["owner", "admin"].includes(activeRole);
  const isOwner = activeRole === "owner";

  return (
    <div className="animate-fade-up">
      <PageHeader overline="Paramètres" title="Configuration"
                  description={`Rôle actuel : ${ROLES.find((r) => r.value === activeRole)?.label || "—"}`} />

      <Tabs defaultValue="account" className="w-full">
        <TabsList className="mb-6 flex flex-wrap h-auto">
          <TabsTrigger value="account" data-testid="settings-tab-account">Mon compte</TabsTrigger>
          <TabsTrigger value="organization" data-testid="settings-tab-org">Organisation</TabsTrigger>
          <TabsTrigger value="members" data-testid="settings-tab-members">Membres & rôles</TabsTrigger>
          <TabsTrigger value="subscription" data-testid="settings-tab-subscription">Abonnement</TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-tab-security">Sécurité & audit</TabsTrigger>
        </TabsList>

        <TabsContent value="account"><AccountTab user={user} refresh={refresh} /></TabsContent>
        <TabsContent value="organization"><OrgTab activeOrg={activeOrg} refresh={refresh} canAdmin={canAdmin} /></TabsContent>
        <TabsContent value="members"><MembersTab canAdmin={canAdmin} /></TabsContent>
        <TabsContent value="subscription"><SubscriptionTab isOwner={isOwner} /></TabsContent>
        <TabsContent value="security"><SecurityTab canAdmin={canAdmin} user={user} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Onglet « Mon compte » — profil personnel + mot de passe + notifications    */
/* -------------------------------------------------------------------------- */
function AccountTab({ user, refresh }) {
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [prefs, setPrefs] = useState(user?.notification_preferences || {
    email_urgent: true, email_weekly: true, email_marketing: false,
    in_app_deadlines: true, in_app_receipts: true,
  });
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || "");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
    setPhone(user?.phone || "");
    setAvatarUrl(user?.avatar_url || "");
    if (user?.notification_preferences) setPrefs(user.notification_preferences);
  }, [user?.id]);

  const save = async () => {
    try {
      await api.patch("/auth/me", {
        first_name: firstName, last_name: lastName, phone,
        notification_preferences: prefs,
      });
      toast.success("Profil mis à jour");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAvatarUrl(data.avatar_url);
      toast.success("Photo de profil mise à jour");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Envoi impossible");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2 card-elevated p-6">
        <div className="text-overline">Identité</div>
        <h2 className="mt-1 font-heading text-xl flex items-center gap-2">
          <User className="h-5 w-5" /> Vos informations personnelles
        </h2>
        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative">
            {avatarUrl ? (
              <img src={avatarUrl} alt="avatar"
                   className="h-20 w-20 rounded-full object-cover ring-2 ring-border" />
            ) : (
              <div className="h-20 w-20 rounded-full bg-secondary flex items-center justify-center
                              ring-2 ring-border text-2xl font-heading text-muted-foreground">
                {(firstName?.[0] || "?") + (lastName?.[0] || "")}
              </div>
            )}
          </div>
          <div>
            <input ref={fileRef} type="file" hidden accept="image/jpeg,image/png,image/webp"
                   onChange={onAvatarPick} data-testid="account-avatar-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                    disabled={uploading} data-testid="account-avatar-btn">
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              {uploading ? "Envoi…" : "Changer la photo"}
            </Button>
            <div className="text-xs text-muted-foreground mt-1.5">JPG, PNG ou WebP — max 3 Mo</div>
          </div>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Prénom</Label>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                   className="mt-1.5" data-testid="account-firstname" />
          </div>
          <div>
            <Label>Nom</Label>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)}
                   className="mt-1.5" data-testid="account-lastname" />
          </div>
          <div>
            <Label>Adresse e-mail</Label>
            <Input value={user?.email || ""} readOnly disabled className="mt-1.5" />
          </div>
          <div>
            <Label>Téléphone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                   placeholder="+33 6 12 34 56 78" className="mt-1.5"
                   data-testid="account-phone" />
          </div>
        </div>
        <div className="mt-6 pt-6 border-t">
          <div className="text-overline mb-3 flex items-center gap-2"><Bell className="h-3.5 w-3.5" /> Notifications</div>
          {[
            ["email_urgent", "E-mails d'alertes urgentes (impayés, échéances critiques)"],
            ["email_weekly", "Récap hebdomadaire par e-mail"],
            ["email_marketing", "Nouveautés et bonnes pratiques Fonbex"],
            ["in_app_deadlines", "Notifications d'échéances dans l'application"],
            ["in_app_receipts", "Rappels de quittances à envoyer"],
          ].map(([k, l]) => (
            <label key={k} className="flex items-center gap-3 py-2 cursor-pointer text-sm">
              <input type="checkbox" checked={!!prefs[k]}
                     onChange={(e) => setPrefs({ ...prefs, [k]: e.target.checked })}
                     data-testid={`account-pref-${k}`} />
              <span>{l}</span>
            </label>
          ))}
        </div>
        <div className="mt-6">
          <Button onClick={save} className="btn-pill" data-testid="account-save">
            <Save className="mr-2 h-4 w-4" />Enregistrer
          </Button>
        </div>
      </div>
      <div>
        <ChangePasswordCard />
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const change = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("Minimum 8 caractères"); return; }
    if (next !== confirm) { toast.error("Confirmation incorrecte"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: cur, new_password: next,
      });
      toast.success("Mot de passe mis à jour");
      setCur(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="card-elevated p-6">
      <div className="text-overline">Sécurité</div>
      <h3 className="mt-1 font-heading text-lg flex items-center gap-2">
        <KeyRound className="h-4 w-4" /> Changer le mot de passe
      </h3>
      <form onSubmit={change} className="mt-4 space-y-3">
        <div>
          <Label>Mot de passe actuel</Label>
          <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)}
                 required autoComplete="current-password" className="mt-1.5"
                 data-testid="account-pwd-current" />
        </div>
        <div>
          <Label>Nouveau mot de passe</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                 required minLength={8} autoComplete="new-password" className="mt-1.5"
                 data-testid="account-pwd-new" />
          <div className="text-xs text-muted-foreground mt-1">Minimum 8 caractères</div>
        </div>
        <div>
          <Label>Confirmer</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                 required minLength={8} autoComplete="new-password" className="mt-1.5"
                 data-testid="account-pwd-confirm" />
        </div>
        <Button type="submit" disabled={busy} className="btn-pill w-full"
                data-testid="account-pwd-submit">
          {busy ? "Modification…" : "Mettre à jour"}
        </Button>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Onglet Organisation étendu — logo + adresse + SIREN + TVA + devise/langue  */
/* -------------------------------------------------------------------------- */
function OrgTab({ activeOrg, refresh, canAdmin }) {
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);

  useEffect(() => {
    if (!activeOrg) return;
    setForm({
      name: activeOrg.name || "",
      logo_url: activeOrg.logo_url || "",
      address_line1: activeOrg.address_line1 || "",
      address_line2: activeOrg.address_line2 || "",
      postal_code: activeOrg.postal_code || "",
      city: activeOrg.city || "",
      country: activeOrg.country || "France",
      phone: activeOrg.phone || "",
      siren: activeOrg.siren || "",
      vat_number: activeOrg.vat_number || "",
      iban: activeOrg.iban || "",
      timezone: activeOrg.timezone || "Europe/Paris",
      currency: activeOrg.currency || "EUR",
      language: activeOrg.language || "fr",
      profile_type: activeOrg.profile_type || "",
      activity_type: activeOrg.activity_type || "",
      contact_email: activeOrg.contact_email || "",
    });
  }, [activeOrg?.id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/organizations/current", form);
      toast.success("Organisation mise à jour");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/org-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, logo_url: data.logo_url }));
      toast.success("Logo mis à jour");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Envoi impossible");
    } finally {
      setUploading(false);
      if (logoRef.current) logoRef.current.value = "";
    }
  };

  return (
    <div className="card-elevated p-6 max-w-4xl">
      <div className="text-overline">Organisation</div>
      <h2 className="mt-1 font-heading text-xl flex items-center gap-2">
        <Building2 className="h-5 w-5" /> Identité et coordonnées
      </h2>

      <div className="mt-6 flex flex-col sm:flex-row items-start gap-4">
        <div className="flex items-center justify-center h-20 w-20 rounded-xl bg-secondary
                        ring-2 ring-border overflow-hidden">
          {form.logo_url ? (
            <img src={form.logo_url} alt="logo" className="h-full w-full object-cover" />
          ) : (
            <Building2 className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
        <div>
          <input ref={logoRef} type="file" hidden accept="image/jpeg,image/png,image/webp"
                 onChange={onLogoPick} data-testid="settings-org-logo-input" />
          <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()}
                  disabled={!canAdmin || uploading} data-testid="settings-org-logo-btn">
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            {uploading ? "Envoi…" : "Changer le logo"}
          </Button>
          <div className="text-xs text-muted-foreground mt-1.5">JPG, PNG ou WebP — max 3 Mo</div>
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Nom de l&apos;organisation</Label>
          <Input value={form.name || ""} onChange={set("name")} disabled={!canAdmin}
                 className="mt-1.5" data-testid="settings-org-name" />
        </div>
        <div>
          <Label>Type de profil patrimonial</Label>
          <Select value={form.profile_type || ""} onValueChange={(v) => setForm((f) => ({ ...f, profile_type: v }))}
                  disabled={!canAdmin}>
            <SelectTrigger className="mt-1.5" data-testid="settings-org-profile"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sci">SCI</SelectItem>
              <SelectItem value="sas">SAS</SelectItem>
              <SelectItem value="holding">Holding</SelectItem>
              <SelectItem value="company">Entreprise</SelectItem>
              <SelectItem value="mdb">Marchand de biens</SelectItem>
              <SelectItem value="private">Patrimoine privé</SelectItem>
              <SelectItem value="investor">Investisseur</SelectItem>
              <SelectItem value="mixed">Mixte</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Adresse — ligne 1</Label>
          <Input value={form.address_line1 || ""} onChange={set("address_line1")} disabled={!canAdmin}
                 className="mt-1.5" placeholder="12 rue de la Paix" data-testid="settings-org-addr1" />
        </div>
        <div>
          <Label>Adresse — ligne 2</Label>
          <Input value={form.address_line2 || ""} onChange={set("address_line2")} disabled={!canAdmin}
                 className="mt-1.5" placeholder="Bâtiment A, 2e étage" />
        </div>
        <div>
          <Label>Code postal</Label>
          <Input value={form.postal_code || ""} onChange={set("postal_code")} disabled={!canAdmin}
                 className="mt-1.5" data-testid="settings-org-zip" />
        </div>
        <div>
          <Label>Ville</Label>
          <Input value={form.city || ""} onChange={set("city")} disabled={!canAdmin}
                 className="mt-1.5" data-testid="settings-org-city" />
        </div>
        <div>
          <Label>Pays</Label>
          <Input value={form.country || ""} onChange={set("country")} disabled={!canAdmin}
                 className="mt-1.5" />
        </div>
        <div>
          <Label>E-mail de contact de l&apos;organisation</Label>
          <Input value={form.contact_email || ""} onChange={set("contact_email")} disabled={!canAdmin}
                 type="email" className="mt-1.5" placeholder="contact@masocie.fr"
                 data-testid="settings-org-contact-email" />
        </div>
        <div>
          <Label>Téléphone</Label>
          <Input value={form.phone || ""} onChange={set("phone")} disabled={!canAdmin}
                 className="mt-1.5" placeholder="+33 1 23 45 67 89" />
        </div>
        <div>
          <Label>SIREN</Label>
          <Input value={form.siren || ""} onChange={set("siren")} disabled={!canAdmin}
                 className="mt-1.5" maxLength={9} placeholder="123456789"
                 data-testid="settings-org-siren" />
        </div>
        <div>
          <Label>N° TVA intracommunautaire</Label>
          <Input value={form.vat_number || ""} onChange={set("vat_number")} disabled={!canAdmin}
                 className="mt-1.5" placeholder="FR12345678901"
                 data-testid="settings-org-vat" />
        </div>
        <div>
          <Label>IBAN</Label>
          <Input value={form.iban || ""} onChange={set("iban")} disabled={!canAdmin}
                 className="mt-1.5 font-mono" placeholder="FR76 3000 6000 0112 3456 7890 189"
                 data-testid="settings-org-iban" />
        </div>
        <div>
          <Label>Fuseau horaire</Label>
          <Select value={form.timezone || "Europe/Paris"}
                  onValueChange={(v) => setForm((f) => ({ ...f, timezone: v }))}
                  disabled={!canAdmin}>
            <SelectTrigger className="mt-1.5" data-testid="settings-org-timezone"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Europe/Paris">Europe/Paris (heure de Paris)</SelectItem>
              <SelectItem value="Europe/Brussels">Europe/Bruxelles</SelectItem>
              <SelectItem value="Europe/Zurich">Europe/Zurich</SelectItem>
              <SelectItem value="Europe/London">Europe/Londres</SelectItem>
              <SelectItem value="Indian/Reunion">Océan Indien (La Réunion)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label>Activité (libellé libre)</Label>
          <Input value={form.activity_type || ""} onChange={set("activity_type")} disabled={!canAdmin}
                 className="mt-1.5" placeholder="Ex : Gestion locative de bureaux et commerces"
                 data-testid="settings-org-activity" />
        </div>
        <div>
          <Label>Devise</Label>
          <Select value={form.currency || "EUR"} disabled>
            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="EUR">Euro (€)</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground mt-1">Autres devises bientôt disponibles</div>
        </div>
        <div>
          <Label>Langue</Label>
          <Select value={form.language || "fr"} onValueChange={(v) => setForm((f) => ({ ...f, language: v }))}
                  disabled={!canAdmin}>
            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fr">Français</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground mt-1">Autres langues bientôt disponibles</div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 text-sm text-muted-foreground">
        <span>Identifiant :</span>
        <span className="font-mono text-foreground">{activeOrg?.slug}</span>
      </div>
      <div className="mt-6">
        <Button onClick={save} disabled={!canAdmin || saving} className="btn-pill"
                data-testid="settings-org-save">
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}

function MembersTab({ canAdmin }) {
  const { user: currentUser, activeRole } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("collaborator");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const isOwner = activeRole === "owner";

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [m, i] = await Promise.all([
        api.get("/organizations/current/members"),
        canAdmin ? api.get("/organizations/current/invitations") : Promise.resolve({ data: { items: [] } }),
      ]);
      setMembers(m.data.items || []);
      setInvitations(i.data.items || []);
    } catch (err) {
      console.error("Load members/invitations failed:", err);
      const msg = err.response?.data?.detail || "Impossible de charger les membres.";
      setLoadError(typeof msg === "string" ? msg : "Impossible de charger les membres.");
      toast.error(typeof msg === "string" ? msg : "Impossible de charger les membres");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const invite = async (e) => {
    e.preventDefault();
    try {
      await api.post("/organizations/current/invitations", { email, role });
      toast.success("Invitation envoyée");
      setEmail("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeRole = async (membershipId, newRole) => {
    try {
      await api.patch(`/organizations/current/members/${membershipId}`, { role: newRole });
      toast.success("Rôle mis à jour");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Retirer ce membre de l'organisation ?")) return;
    try { await api.delete(`/organizations/current/members/${id}`); toast.success("Membre retiré"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const revokeInvitation = async (id) => {
    if (!window.confirm("Révoquer cette invitation en attente ?")) return;
    try { await api.delete(`/organizations/current/invitations/${id}`); toast.success("Invitation révoquée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const roleLabel = (v) => ROLES.find((r) => r.value === v)?.label || v;

  return (
    <div className="space-y-6">
      {canAdmin && (
        <div className="card-elevated p-6">
          <div className="text-overline">Inviter</div>
          <h2 className="mt-1 font-heading text-xl">Ajouter un membre à l&apos;organisation</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Une invitation par e-mail sera envoyée ; le destinataire crée son mot de passe et rejoint l&apos;organisation.
          </p>
          <form onSubmit={invite} className="mt-6 grid gap-3 sm:grid-cols-[1fr_200px_auto]">
            <Input placeholder="email@exemple.fr" value={email} onChange={(e) => setEmail(e.target.value)}
                   required type="email" data-testid={TID.settingsInviteEmail} />
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid={TID.settingsInviteRole}><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.filter((r) => r.value !== "owner" && r.value !== "tenant").map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" className="btn-pill" data-testid={TID.settingsInviteSubmit}>
              <UserPlus className="mr-2 h-4 w-4" /> Inviter
            </Button>
          </form>
        </div>
      )}

      <div className="card-elevated">
        <div className="px-4 sm:px-6 py-4 border-b border-border font-heading flex items-center gap-2">
          <Shield className="h-4 w-4 text-accent" />
          Membres actifs {loading ? "" : `(${members.length})`}
        </div>
        {loading || loadError ? (
          <SmartLoader label="Chargement des membres…" onRetry={load} error={loadError} />
        ) : members.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            Aucun membre pour l&apos;instant. Invitez votre équipe pour commencer.
          </div>
        ) : (
          <>
            {/* Desktop table — hidden below md */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-6 py-3 text-left">Nom</th>
                    <th className="px-6 py-3 text-left">E-mail</th>
                    <th className="px-6 py-3 text-left">Rôle</th>
                    <th className="px-6 py-3 text-left">Ajouté le</th>
                    <th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {members.map((m) => {
                    const isSelf = m.user_id === currentUser?.id;
                    const isRowOwner = m.role === "owner";
                    const canEditRow = canAdmin && !isRowOwner && !isSelf;
                    return (
                      <tr key={m.id} className="hover:bg-secondary/30" data-testid={`member-row-${m.id}`}>
                        <td className="px-6 py-3 font-medium">
                          <div className="flex items-center gap-2">
                            <span>{(m.first_name || "") + " " + (m.last_name || "")}</span>
                            {isSelf && (
                              <span className="text-[10px] font-bold uppercase tracking-widest text-accent bg-accent/10 px-2 py-0.5 rounded-full">Vous</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-muted-foreground">{m.email}</td>
                        <td className="px-6 py-3">
                          {canEditRow ? (
                            <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                              <SelectTrigger className="w-40 h-8" data-testid={`member-role-select-${m.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ROLES.filter((r) => r.value !== "owner" && r.value !== "tenant").map((r) => (
                                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <StatusBadge tone={isRowOwner ? "accent" : "neutral"}>
                              {roleLabel(m.role)}
                            </StatusBadge>
                          )}
                        </td>
                        <td className="px-6 py-3 text-muted-foreground text-xs">
                          {formatDateFr(m.created_at)}
                        </td>
                        <td className="px-6 py-3 text-right">
                          {canAdmin && !isRowOwner && !isSelf && (
                            <Button variant="ghost" size="sm" onClick={() => remove(m.id)}
                                    data-testid={`member-remove-${m.id}`}
                                    aria-label="Retirer ce membre">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile cards — visible below md */}
            <ul className="md:hidden divide-y divide-border">
              {members.map((m) => {
                const isSelf = m.user_id === currentUser?.id;
                const isRowOwner = m.role === "owner";
                const canEditRow = canAdmin && !isRowOwner && !isSelf;
                return (
                  <li key={m.id} className="px-4 py-4 space-y-2"
                      data-testid={`member-card-${m.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">
                            {(m.first_name || "") + " " + (m.last_name || "")}
                          </span>
                          {isSelf && (
                            <span className="text-[10px] font-bold uppercase tracking-widest text-accent bg-accent/10 px-2 py-0.5 rounded-full">Vous</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate mt-0.5">{m.email}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                          Ajouté le {formatDateFr(m.created_at)}
                        </div>
                      </div>
                      {canAdmin && !isRowOwner && !isSelf && (
                        <Button variant="ghost" size="icon" onClick={() => remove(m.id)}
                                data-testid={`member-remove-mobile-${m.id}`}
                                aria-label="Retirer ce membre">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                    {canEditRow ? (
                      <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                        <SelectTrigger className="h-9" data-testid={`member-role-select-mobile-${m.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.filter((r) => r.value !== "owner" && r.value !== "tenant").map((r) => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <StatusBadge tone={isRowOwner ? "accent" : "neutral"}>
                        {roleLabel(m.role)}
                      </StatusBadge>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {canAdmin && invitations.length > 0 && (
        <div className="card-elevated">
          <div className="px-4 sm:px-6 py-4 border-b border-border font-heading">
            Invitations en attente ({invitations.length})
          </div>
          <ul className="divide-y divide-border">
            {invitations.map((i) => (
              <li key={i.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 text-sm"
                  data-testid={`invitation-row-${i.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{i.email}</div>
                  <div className="text-xs text-muted-foreground">Rôle : {roleLabel(i.role)} — Envoyée le {formatDateFr(i.created_at)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge tone="warning">En attente</StatusBadge>
                  {isOwner && (
                    <Button variant="ghost" size="sm" onClick={() => revokeInvitation(i.id)}
                            data-testid={`invitation-revoke-${i.id}`}
                            aria-label="Révoquer l'invitation">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!canAdmin && (
        <div className="text-xs text-muted-foreground">
          Seuls les administrateurs et propriétaires peuvent gérer les membres.
        </div>
      )}
    </div>
  );
}

function SubscriptionTab({ isOwner }) {
  const [current, setCurrent] = useState(null);
  const [plans, setPlans] = useState(null);
  const [managing, setManaging] = useState(false);
  useEffect(() => {
    api.get("/subscription").then(({ data }) => setCurrent(data));
    api.get("/payments/plans").then(({ data }) => setPlans(data)).catch(() => setPlans(null));
  }, []);

  const openPortal = async () => {
    setManaging(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Portail Stripe indisponible");
    } finally { setManaging(false); }
  };

  const activeStripe = current?.status === "active";

  return (
    <div className="space-y-6">
      <div className="card-elevated p-6">
        <div className="text-overline">Formule active</div>
        <div className="mt-2 flex items-baseline gap-3 flex-wrap">
          <span className="font-serif text-3xl capitalize">{current?.plan || "—"}</span>
          <StatusBadge tone={current?.status === "trial" ? "warning" : activeStripe ? "success" : "neutral"}>
            {current?.status === "trial" ? "Essai en cours"
              : activeStripe ? "Actif"
              : current?.status || "—"}
          </StatusBadge>
        </div>
        {current?.trial_ends_at && (
          <div className="mt-2 text-sm text-muted-foreground">Fin d&apos;essai : {formatDateTimeFr(current.trial_ends_at)}</div>
        )}
      </div>

      {activeStripe ? (
        <div className="card-elevated p-6">
          <h3 className="font-heading text-lg">Gérer mon abonnement</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Modifier votre moyen de paiement, télécharger vos factures ou résilier
            depuis le portail sécurisé Stripe.
          </p>
          <Button onClick={openPortal} disabled={managing} className="btn-pill mt-4"
                  data-testid="subscription-portal-btn">
            {managing ? "Redirection…" : "Ouvrir le portail Stripe"}
          </Button>
        </div>
      ) : plans?.monthly ? (
        <div className="card-elevated p-6">
          <h3 className="font-heading text-lg">Fonbex Pro</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Un seul plan tout-inclus — {(plans.monthly.unit_amount / 100).toFixed(0)} €/mois
            ou {(plans.yearly.unit_amount / 100).toFixed(0)} €/an
            (soit ~20 % d&apos;économie). Résiliation à tout moment.
          </p>
          <Button asChild disabled={!isOwner} className="btn-pill mt-4"
                  data-testid="subscription-cta-btn">
            <Link to="/app/subscription">Voir les formules & s&apos;abonner</Link>
          </Button>
          {!isOwner && (
            <p className="mt-3 text-xs text-muted-foreground">
              Seul le propriétaire du compte peut souscrire l&apos;abonnement.
            </p>
          )}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">Chargement des formules…</div>
      )}
    </div>
  );
}

function SecurityTab({ canAdmin, user }) {
  const [logs, setLogs] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/auth/login-history").then(({ data }) => setLoginHistory(data.items || []))
      .catch(() => setLoginHistory([]));
    if (!canAdmin) { setLoading(false); return; }
    api.get("/audit-logs?limit=50").then(({ data }) => setLogs(data.items || [])).finally(() => setLoading(false));
  }, [canAdmin]);
  return (
    <div className="space-y-6">
      <div className="card-elevated p-6 max-w-2xl">
        <div className="text-overline">Sécurité du compte</div>
        <h2 className="mt-1 font-heading text-xl">Vérification & authentification</h2>
        <div className="mt-4 space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span>Adresse e-mail</span>
            <StatusBadge tone={user?.email_verified ? "success" : "warning"}>
              {user?.email_verified ? "Vérifiée" : "Non vérifiée"}
            </StatusBadge>
          </div>
          <div className="flex items-center justify-between">
            <span>Double authentification</span>
            <StatusBadge tone="neutral">Prochainement</StatusBadge>
          </div>
          <div className="flex items-center justify-between">
            <span>Connexion Google / Microsoft</span>
            <StatusBadge tone="neutral">Prochainement</StatusBadge>
          </div>
        </div>
      </div>

      <div className="card-elevated overflow-x-auto">
        <div className="px-6 py-4 border-b border-border font-heading">
          Journal de vos connexions — 30 dernières tentatives
        </div>
        <table className="w-full text-sm" data-testid="security-login-history">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-6 py-3 text-left">Horodatage</th>
              <th className="px-6 py-3 text-left">IP</th>
              <th className="px-6 py-3 text-left">Résultat</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loginHistory.length === 0 && (
              <tr><td colSpan={3} className="px-6 py-8 text-center text-muted-foreground">
                Aucune tentative enregistrée.
              </td></tr>
            )}
            {loginHistory.map((l) => (
              <tr key={l.id} className="hover:bg-secondary/30">
                <td className="px-6 py-3 text-xs font-mono text-muted-foreground">
                  {formatDateTimeFr(l.created_at)}
                </td>
                <td className="px-6 py-3 font-mono text-xs">{l.ip || "—"}</td>
                <td className="px-6 py-3">
                  <StatusBadge tone={l.success ? "success" : "error"}>
                    {l.success ? "Réussie" : "Échec"}
                  </StatusBadge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canAdmin && (
        <div className="card-elevated overflow-x-auto">
          <div className="px-6 py-4 border-b border-border font-heading">Journal d&apos;audit — 50 derniers événements</div>
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-6 py-3 text-left">Date</th><th className="px-6 py-3 text-left">Action</th><th className="px-6 py-3 text-left">Utilisateur</th><th className="px-6 py-3 text-left">IP</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && <tr><td colSpan={4} className="px-6 py-8 text-center text-muted-foreground">Chargement…</td></tr>}
              {logs.map((l) => (
                <tr key={l.id} className="hover:bg-secondary/30">
                  <td className="px-6 py-3 text-xs font-mono text-muted-foreground">{formatDateTimeFr(l.created_at)}</td>
                  <td className="px-6 py-3 font-medium">{l.action}</td>
                  <td className="px-6 py-3 text-muted-foreground">{l.user_id?.slice(0, 8) || "—"}</td>
                  <td className="px-6 py-3 font-mono text-xs text-muted-foreground">{l.ip || "—"}</td>
                </tr>
              ))}
              {!loading && logs.length === 0 && <tr><td colSpan={4} className="px-6 py-8 text-center text-muted-foreground">Aucun événement.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
